import React, { useState } from 'react';
import { Buildings, Eye, PencilSimple, ChatCircle, Camera, Plus, Trash, X, Receipt, ArrowRight, ShoppingCart, GasPump, Hammer, Truck, Package, DotsThree, FilePdf, PaperPlaneTilt, CheckCircle, Warning } from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';
import { PdfViewerModal } from '../invoicing/PdfViewerModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Visual config for expense category breakdown shown on the project sheet
const CATEGORY_VIEW = {
  materials:     { label: 'Achats / Materiaux', icon: ShoppingCart, color: '#1565C0', bg: '#E3F2FD' },
  fuel:          { label: 'Carburant',          icon: GasPump,      color: '#EF6C00', bg: '#FFF3E0' },
  subcontractor: { label: 'Sous-traitance',     icon: Hammer,       color: '#6A1B9A', bg: '#F3E5F5' },
  rental:        { label: 'Location',           icon: Package,      color: '#0277BD', bg: '#E1F5FE' },
  logistics:     { label: 'Logistique',         icon: Truck,        color: '#2E7D32', bg: '#E8F5E9' },
  other:         { label: 'Autre',              icon: DotsThree,    color: '#5E5F66', bg: '#F4F5F7' },
};
const LABOR_VIEW = { label: 'Main-d\'oeuvre', icon: Hammer, color: '#C62828', bg: '#FFEBEE' };

const ExpensesBreakdown = ({ project }) => {
  const byCat = project.expenses_by_category || {};
  const laborCost = project.labor_cost_ht || 0;
  const totalHours = project.total_hours || 0;

  // Show every category that has a non-zero amount, plus labor at the end
  const items = [];
  const ordered = ['materials', 'fuel', 'subcontractor', 'rental', 'logistics', 'other'];
  for (const k of ordered) {
    const v = byCat[k];
    if (v && v > 0) items.push({ key: k, label: CATEGORY_VIEW[k]?.label || k, amount: v, ...CATEGORY_VIEW[k] });
  }
  // Add any unknown categories from backend (defensive)
  Object.entries(byCat).forEach(([k, v]) => {
    if (!ordered.includes(k) && v > 0) {
      items.push({ key: k, label: k, amount: v, icon: DotsThree, color: '#5E5F66', bg: '#F4F5F7' });
    }
  });
  if (laborCost > 0) {
    items.push({ key: 'labor', label: `${LABOR_VIEW.label} (${totalHours}h)`, amount: laborCost, icon: LABOR_VIEW.icon, color: LABOR_VIEW.color, bg: LABOR_VIEW.bg });
  }

  // Linked employee expenses (notes de frais + carte société) attached to this chantier
  const empExp = project.employee_expenses_linked_ht || 0;
  if (empExp > 0) {
    items.push({ key: 'staff_expenses', label: 'Frais salariés', amount: empExp, icon: Receipt, color: '#7B1FA2', bg: '#F3E5F5' });
  }

  if (items.length === 0) return null;

  const total = items.reduce((s, it) => s + (it.amount || 0), 0);

  return (
    <div className="card" style={{ marginBottom: 16, padding: '14px 18px' }} data-testid="project-expenses-breakdown">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10, flexWrap: 'wrap', gap: 8 }}>
        <h3 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 700 }}>Depenses realisees pour le chantier</h3>
        <div style={{ fontSize: '0.85rem', color: '#5E5F66' }}>
          Total : <strong style={{ color: '#1E2030' }}>{total.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} EUR</strong>
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 10 }}>
        {items.map(it => {
          const Icon = it.icon || DotsThree;
          const pct = total > 0 ? Math.round((it.amount / total) * 1000) / 10 : 0;
          return (
            <div key={it.key} data-testid={`exp-cat-${it.key}`} style={{ padding: '10px 12px', borderRadius: 8, backgroundColor: it.bg, borderLeft: `3px solid ${it.color}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.72rem', color: it.color, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.3 }}>
                <Icon size={14} weight="bold" /> {it.label}
              </div>
              <div style={{ fontSize: '1.2rem', fontWeight: 800, color: '#1E2030', marginTop: 4 }}>
                {it.amount.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} EUR
              </div>
              <div style={{ fontSize: '0.7rem', color: '#6B7280', marginTop: 2 }}>
                {pct.toFixed(1)}% du total
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const STATUS_CONFIG = {
  brouillon: { label: 'Brouillon', color: '#5E5F66', bg: '#F4F5F7' },
  acceptee: { label: 'Acceptee', color: '#92400E', bg: '#FEF3C7' },
  emise: { label: 'Emise', color: '#1565C0', bg: '#E3F2FD' },
  payee: { label: 'Payee', color: '#2E7D32', bg: '#E8F5E9' },
  annulee: { label: 'Annulee', color: '#C62828', bg: '#FFEBEE' }
};

export const ProjectList = ({ projects, selectedId, onSelect }) => (
  <div className="card" style={{ maxHeight: 'calc(100vh - 200px)', overflow: 'auto' }}>
    <div className="card-header">
      <h3 className="card-title">Chantiers ({projects.length})</h3>
    </div>
    <div style={{ padding: '0' }}>
      {projects.map((project) => {
        const progressPct = project.sale_price_ht > 0 ? Math.min(100, Math.round(project.total_invoiced_ht / project.sale_price_ht * 100)) : 0;
        const barColor = progressPct >= 100 ? '#2E7D32' : progressPct >= 50 ? '#1565C0' : '#EF6C00';
        return (
          <div key={project.id} onClick={() => onSelect(project)} data-testid={`project-card-${project.id}`}
            style={{ padding: '16px', borderBottom: '1px solid #E2E2E6', cursor: 'pointer', backgroundColor: selectedId === project.id ? '#F4F5F7' : 'white' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: '4px' }}>
              <div style={{ fontWeight: '600', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{project.name}</div>
              {project.margin_alert_active && (
                <span data-testid={`margin-alert-badge-${project.id}`}
                  title={`Coût réel ${project.margin_consumed_pct}% du prix de vente HT (seuil ${project.margin_alert_threshold_pct}%)`}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: '0.68rem', padding: '2px 6px', borderRadius: 10, background: '#FFEBEE', color: '#C62828', fontWeight: 700, whiteSpace: 'nowrap' }}>
                  <Warning size={11} weight="fill" /> Marge
                </span>
              )}
            </div>
            <div style={{ fontSize: '0.75rem', color: '#5E5F66' }}>{project.client_name}</div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px', fontSize: '0.75rem' }}>
              <span>Vente: {project.sale_price_ht.toLocaleString()}euro</span>
              <span style={{ color: project.remaining_ht >= 0 ? '#2E7D32' : '#C62828' }}>Reste: {project.remaining_ht.toLocaleString()}euro</span>
            </div>
            <div style={{ marginTop: '8px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.7rem', marginBottom: '3px' }}>
                <span style={{ color: '#5E5F66' }}>Avancement facturation</span>
                <span style={{ fontWeight: '700', color: barColor }}>{progressPct}%</span>
              </div>
              <div style={{ height: '4px', backgroundColor: '#E2E2E6', borderRadius: '2px', overflow: 'hidden' }}>
                <div data-testid={`progress-bar-${project.id}`} style={{ height: '100%', width: `${progressPct}%`, backgroundColor: barColor, borderRadius: '2px', transition: 'width 0.4s ease' }} />
              </div>
            </div>
          </div>
        );
      })}
      {projects.length === 0 && (
        <div style={{ padding: '40px', textAlign: 'center', color: '#5E5F66' }}>Aucun chantier</div>
      )}
    </div>
  </div>
);

export const ProjectSummary = ({ project, employeeExpenses, onReload }) => {
  const pct = project.sale_price_ht > 0 ? Math.min(100, Math.round(project.total_invoiced_ht / project.sale_price_ht * 100)) : 0;
  const pctColor = pct >= 100 ? '#2E7D32' : pct >= 50 ? '#1565C0' : '#EF6C00';
  
  // Cost vs Quote (ecart)
  const coutReel = (project.total_expenses_ht || 0) + (project.labor_cost_ht || 0);
  const margePrevue = (project.sale_price_ht || 0) - coutReel;
  const margeReelle = (project.total_invoiced_ht || 0) - coutReel;
  const ecartFacturation = (project.total_invoiced_ht || 0) - (project.sale_price_ht || 0);
  const ecartPct = project.sale_price_ht > 0 ? (ecartFacturation / project.sale_price_ht) * 100 : 0;
  const hasQuote = (project.sale_price_ht || 0) > 0;
  
  return (
    <>
      {project.autoliquidation_tva && (
        <div data-testid="project-autoliq-banner" style={{
          background: 'linear-gradient(90deg, #C62828 0%, #B71C1C 100%)',
          color: 'white',
          padding: '12px 18px',
          borderRadius: '10px',
          marginBottom: '16px',
          fontWeight: 800,
          fontSize: '0.95rem',
          letterSpacing: '0.6px',
          textAlign: 'center',
          boxShadow: '0 4px 12px rgba(198, 40, 40, 0.3)',
          textTransform: 'uppercase',
        }}>
          ⚠ TVA 0% AUTOLIQUIDATION ⚠
        </div>
      )}
      {project.margin_alert_active && (
        <div data-testid="project-margin-alert-banner" style={{
          background: 'linear-gradient(90deg, #B71C1C 0%, #C62828 100%)',
          color: 'white',
          padding: '12px 18px',
          borderRadius: '10px',
          marginBottom: '16px',
          fontWeight: 700,
          fontSize: '0.92rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 10,
          boxShadow: '0 4px 12px rgba(183, 28, 28, 0.3)',
        }}>
          <Warning size={20} weight="fill" />
          MARGE SOUS TENSION — Le coût réel atteint {project.margin_consumed_pct}% du prix de vente HT
          (seuil d'alerte : {project.margin_alert_threshold_pct}%)
        </div>
      )}
      <div className="stats-grid" style={{ marginBottom: '16px', gridTemplateColumns: 'repeat(5, 1fr)' }}>
        <div className="stat-card">
          <div className="stat-label">Prix de vente HT</div>
          <div className="stat-value" style={{ fontSize: '1.5rem' }}>{project.sale_price_ht.toLocaleString()}euro</div>
          {(project.accepted_avenants_total_ht || 0) > 0 && (
            <div data-testid="project-avenants-breakdown" style={{ fontSize: '0.72rem', color: '#5E5F66', marginTop: '4px', lineHeight: 1.35 }}>
              <div>Base&nbsp;: {(project.base_sale_price_ht || 0).toLocaleString()}&nbsp;EUR</div>
              <div style={{ color: '#2E7D32', fontWeight: 600 }}>+ {project.accepted_avenants_total_ht.toLocaleString()}&nbsp;EUR avenants</div>
            </div>
          )}
        </div>
        <div className="stat-card"><div className="stat-label">Depenses HT</div><div className="stat-value" style={{ fontSize: '1.5rem', color: '#C62828' }}>{project.total_expenses_ht.toLocaleString()}euro</div></div>
        <div className="stat-card"><div className="stat-label">Main-d'oeuvre ({project.total_hours}h)</div><div className="stat-value" style={{ fontSize: '1.5rem', color: '#EF6C00' }}>{(project.labor_cost_ht || 0).toLocaleString()}euro</div></div>
        <div className="stat-card"><div className="stat-label">Facture HT</div><div className="stat-value" style={{ fontSize: '1.5rem', color: '#2E7D32' }}>{project.total_invoiced_ht.toLocaleString()}euro</div></div>
        <div className="stat-card"><div className="stat-label">Reste HT</div><div className="stat-value" style={{ fontSize: '1.5rem', color: project.remaining_ht >= 0 ? '#2E7D32' : '#C62828' }}>{project.remaining_ht.toLocaleString()}euro</div></div>
      </div>

      {/* Dépenses détaillées par catégorie + main d'œuvre */}
      <ExpensesBreakdown project={project} />

      {/* Main-d'œuvre : édition de l'override + détail du calcul auto */}
      <LaborCostPanel project={project} onUpdated={onReload} />

      {/* Frais salariés (notes de frais + carte société) liés au chantier */}
      {(employeeExpenses?.length || 0) > 0 && (
        <div className="card" style={{ marginBottom: 16 }} data-testid="project-employee-expenses-card">
          <div className="card-header">
            <h3 className="card-title">Frais salariés liés au chantier ({employeeExpenses.length})</h3>
          </div>
          <table className="data-table">
            <thead><tr><th>Date</th><th>Salarié</th><th>Description</th><th>Type</th><th>HT</th><th>TTC</th><th>Statut</th></tr></thead>
            <tbody>
              {employeeExpenses.map((e) => (
                <tr key={e.id} data-testid={`linked-emp-exp-${e.id}`}>
                  <td>{e.date}</td>
                  <td>{e.user_name}</td>
                  <td>{e.description}</td>
                  <td>
                    <span style={{
                      fontSize: '0.7rem', padding: '2px 6px', borderRadius: 3, fontWeight: 600,
                      background: e.paid_by === 'company_card' ? '#E3F2FD' : '#FFF3E0',
                      color: e.paid_by === 'company_card' ? '#1565C0' : '#EF6C00',
                    }}>
                      {e.paid_by === 'company_card' ? 'Carte société' : 'Avance salarié'}
                    </span>
                  </td>
                  <td>{Number(e.amount_ht || 0).toFixed(2)}euro</td>
                  <td style={{ fontWeight: 600 }}>{Number(e.amount_ttc || 0).toFixed(2)}euro</td>
                  <td>
                    <span style={{
                      fontSize: '0.7rem', padding: '2px 6px', borderRadius: 3, fontWeight: 600,
                      background: e.status === 'approved' ? '#E8F5E9' : e.status === 'rejected' ? '#FFEBEE' : '#FEF3C7',
                      color: e.status === 'approved' ? '#2E7D32' : e.status === 'rejected' ? '#C62828' : '#92400E',
                    }}>
                      {e.status === 'approved' ? 'Approuvé' : e.status === 'rejected' ? 'Refusé' : 'En attente'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="inline-soft-bg" style={{ marginBottom: '16px', padding: '12px 16px', borderRadius: '6px' }} data-testid="project-progress-bar">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
          <span className="inline-soft-title" style={{ fontSize: '0.8rem', fontWeight: '600' }}>Avancement facturation</span>
          <span style={{ fontSize: '0.9rem', fontWeight: '700', color: pctColor }}>{pct}%</span>
        </div>
        <div className="inline-track" style={{ height: '8px', borderRadius: '4px', overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${pct}%`, backgroundColor: pctColor, borderRadius: '4px', transition: 'width 0.5s ease' }} />
        </div>
        <div className="inline-soft-muted" style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px', fontSize: '0.7rem' }}>
          <span>0euro</span>
          <span>{project.sale_price_ht.toLocaleString()}euro</span>
        </div>
      </div>

      {hasQuote && (
        <div className="inline-soft-panel" style={{ marginBottom: '24px', padding: '14px 18px', borderRadius: '6px' }} data-testid="project-quote-variance">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
            <span className="inline-soft-title" style={{ fontSize: '0.85rem', fontWeight: '700' }}>Ecart sur devis</span>
            <span className="inline-soft-chip" style={{ fontSize: '0.7rem', padding: '2px 6px', borderRadius: '3px' }}>Analyse chiffrage</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px' }}>
            <VarianceCard label="Marge prevue (sur devis)" value={margePrevue} hint={`${(project.sale_price_ht || 0).toLocaleString()} - ${coutReel.toLocaleString()}`} />
            <VarianceCard label="Marge realisee (facturee)" value={margeReelle} hint={`${(project.total_invoiced_ht || 0).toLocaleString()} - ${coutReel.toLocaleString()}`} />
            <VarianceCard label="Ecart facturation / devis" value={ecartFacturation} pct={ecartPct} pctPositive={ecartFacturation >= 0} />
          </div>
        </div>
      )}
    </>
  );
};

const VarianceCard = ({ label, value, pct, pctPositive, hint }) => {
  const color = value >= 0 ? '#2E7D32' : '#C62828';
  return (
    <div className="inline-variance-card" style={{ padding: '10px 12px', borderRadius: '4px' }}>
      <div style={{ fontSize: '0.72rem', color: '#5E5F66', marginBottom: '4px' }}>{label}</div>
      <div style={{ fontSize: '1.2rem', fontWeight: '700', color }}>{value.toLocaleString()}euro</div>
      {pct !== undefined && (
        <div style={{ fontSize: '0.72rem', fontWeight: '600', color: pctPositive ? '#2E7D32' : '#C62828', marginTop: '2px' }}>
          {pct >= 0 ? '+' : ''}{pct.toFixed(1)}% vs devis
        </div>
      )}
      {hint && <div style={{ fontSize: '0.68rem', color: '#9E9E9E', marginTop: '2px' }}>{hint}euro</div>}
    </div>
  );
};

// Inline editor for the labor cost panel (admin override)
const LaborCostPanel = ({ project, onUpdated }) => {
  const auto = project.labor_cost_auto_ht || 0;
  const override = project.labor_cost_override_ht;
  const coef = project.labor_cost_coefficient || 1.42;
  const hours = project.total_hours || 0;
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(override != null ? String(override) : '');
  const [saving, setSaving] = useState(false);

  const save = async (raw) => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/projects/${project.id}`,
        { labor_cost_override: raw === '' || raw == null ? null : Number(raw) },
        { withCredentials: true });
      setEditing(false);
      onUpdated?.();
    } catch {
      toast.error('Erreur sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card" style={{ marginBottom: '16px', padding: '14px 18px' }} data-testid="project-labor-cost-panel">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <div>
          <h3 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 700, color: '#C62828' }}>
            <Hammer size={16} weight="bold" style={{ display: 'inline', verticalAlign: '-2px', marginRight: 4 }} />
            Main-d'œuvre du chantier
          </h3>
          <div style={{ fontSize: '0.78rem', color: '#5E5F66', marginTop: 4 }}>
            Calcul auto: <strong>{auto.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} EUR</strong>{' '}
            ({hours}h pointées × taux journalier × coef {coef.toFixed(2)})
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {!editing && (
            <>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '0.72rem', color: '#5E5F66', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                  Coût retenu HT {override != null && <span style={{ color: '#EF6C00' }}>(manuel)</span>}
                </div>
                <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#1E2030' }}>
                  {(project.labor_cost_ht || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} EUR
                </div>
              </div>
              <button className="btn btn-secondary btn-sm" onClick={() => { setValue(override != null ? String(override) : ''); setEditing(true); }} data-testid="labor-override-edit-btn">
                <PencilSimple size={14} />
              </button>
            </>
          )}
          {editing && (
            <>
              <input
                type="number" step="0.01" min="0" placeholder="Auto"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                data-testid="labor-override-input"
                className="form-input" style={{ width: 140, height: 32 }}
              />
              <button className="btn btn-primary btn-sm" onClick={() => save(value)} disabled={saving} data-testid="labor-override-save-btn">
                {saving ? '…' : 'Enregistrer'}
              </button>
              <button className="btn btn-secondary btn-sm" onClick={() => save('')} disabled={saving} data-testid="labor-override-reset-btn" title="Réinitialiser au calcul auto">
                Auto
              </button>
              <button className="btn btn-secondary btn-sm" onClick={() => setEditing(false)} disabled={saving} data-testid="labor-override-cancel-btn">
                <X size={14} />
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export const ExpensesTab = ({ expenses, getCategoryLabel, onDelete, project, employeeExpenses, onReload }) => {
  const linkedExp = employeeExpenses || [];
  return (
    <>
      {project && <LaborCostPanel project={project} onUpdated={onReload} />}
      <div className="card" style={{ marginBottom: '24px' }}>
        <div className="card-header"><h3 className="card-title">Dépenses chantier ({expenses.length})</h3></div>
        {expenses.length > 0 ? (
          <table className="data-table">
            <thead><tr><th>Date</th><th>Categorie</th><th>Description</th><th>HT</th><th>TVA</th><th>TTC</th><th>Heures</th><th></th></tr></thead>
            <tbody>
              {expenses.map((exp) => (
                <tr key={exp.id}>
                  <td>{exp.date}</td>
                  <td>{getCategoryLabel(exp.category)}</td>
                  <td>{exp.description}</td>
                  <td>{exp.amount_ht.toFixed(2)}euro</td>
                  <td>{exp.amount_tva.toFixed(2)}euro</td>
                  <td style={{ fontWeight: '600' }}>{exp.amount_ttc.toFixed(2)}euro</td>
                  <td>{exp.hours || '-'}</td>
                  <td><button className="btn btn-danger btn-sm btn-icon" onClick={() => onDelete(exp.id)} data-testid={`delete-exp-${exp.id}`}><Trash size={14} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div style={{ padding: '24px', textAlign: 'center', color: '#5E5F66' }}>Aucune depense</div>
        )}
      </div>

      {linkedExp.length > 0 && (
        <div className="card" style={{ marginBottom: '24px' }} data-testid="project-employee-expenses-card">
          <div className="card-header">
            <h3 className="card-title">Frais salariés liés ({linkedExp.length})</h3>
          </div>
          <table className="data-table">
            <thead><tr><th>Date</th><th>Salarié</th><th>Description</th><th>Type</th><th>HT</th><th>TTC</th><th>Statut</th></tr></thead>
            <tbody>
              {linkedExp.map((e) => (
                <tr key={e.id} data-testid={`linked-emp-exp-${e.id}`}>
                  <td>{e.date}</td>
                  <td>{e.user_name}</td>
                  <td>{e.description}</td>
                  <td>
                    <span style={{
                      fontSize: '0.7rem', padding: '2px 6px', borderRadius: 3, fontWeight: 600,
                      background: e.paid_by === 'company_card' ? '#E3F2FD' : '#FFF3E0',
                      color: e.paid_by === 'company_card' ? '#1565C0' : '#EF6C00',
                    }}>
                      {e.paid_by === 'company_card' ? 'Carte société' : 'Avance salarié'}
                    </span>
                  </td>
                  <td>{Number(e.amount_ht || 0).toFixed(2)}euro</td>
                  <td style={{ fontWeight: 600 }}>{Number(e.amount_ttc || 0).toFixed(2)}euro</td>
                  <td>
                    <span style={{
                      fontSize: '0.7rem', padding: '2px 6px', borderRadius: 3, fontWeight: 600,
                      background: e.status === 'approved' ? '#E8F5E9' : e.status === 'rejected' ? '#FFEBEE' : '#FEF3C7',
                      color: e.status === 'approved' ? '#2E7D32' : e.status === 'rejected' ? '#C62828' : '#92400E',
                    }}>
                      {e.status === 'approved' ? 'Approuvé' : e.status === 'rejected' ? 'Refusé' : 'En attente'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
};

// In-place situation consult modal — fetches the full invoice and shows lines/advancements
const SituationConsultModal = ({ invoiceId, onClose }) => {
  const [inv, setInv] = useState(null);
  const [loading, setLoading] = useState(true);
  React.useEffect(() => {
    setLoading(true);
    axios.get(`${API_URL}/api/invoicing/${invoiceId}`, { withCredentials: true })
      .then(r => setInv(r.data))
      .catch(() => setInv(null))
      .finally(() => setLoading(false));
  }, [invoiceId]);
  return (
    <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: 20 }} onClick={onClose} data-testid="situation-consult-overlay">
      <div className="card" style={{ width: '100%', maxWidth: '1000px', maxHeight: '92vh', overflow: 'auto' }} onClick={(e) => e.stopPropagation()} data-testid="situation-consult-modal">
        <div style={{ padding: '14px 20px', borderBottom: '1px solid #E2E2E6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '1.05rem' }}>{loading ? 'Chargement...' : `Situation ${inv?.number || ''}`}</h3>
            {!loading && inv && (
              <p style={{ margin: '2px 0 0', fontSize: '0.82rem', color: '#5E5F66' }}>
                {inv.date}{inv.quote_number ? ` - depuis devis ${inv.quote_number}` : ''}{inv.client_name ? ` - ${inv.client_name}` : ''}
              </p>
            )}
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }} data-testid="situation-close-btn"><X size={20} /></button>
        </div>
        {loading && <div style={{ padding: 40, textAlign: 'center' }}>Chargement...</div>}
        {!loading && !inv && <div style={{ padding: 40, textAlign: 'center', color: '#C62828' }}>Impossible de charger la situation.</div>}
        {!loading && inv && (
          <>
            <div className="stats-grid" style={{ padding: '14px 20px', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, borderBottom: '1px solid #F0F1F3' }}>
              <div className="stat-card"><div className="stat-label">Avancement HT</div><div className="stat-value" style={{ fontSize: '1.3rem' }}>{(inv.total_ht || 0).toLocaleString('fr-FR')} EUR</div></div>
              <div className="stat-card"><div className="stat-label">TVA</div><div className="stat-value" style={{ fontSize: '1.3rem', color: '#EF6C00' }}>{(inv.total_tva || 0).toLocaleString('fr-FR')} EUR</div></div>
              <div className="stat-card"><div className="stat-label">Avancement TTC</div><div className="stat-value" style={{ fontSize: '1.3rem', color: '#2E7D32' }}>{(inv.total_ttc || 0).toLocaleString('fr-FR')} EUR</div></div>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table className="data-table" data-testid="situation-lines-table">
                <thead>
                  <tr>
                    <th>Description</th>
                    <th style={{ textAlign: 'right' }}>Prix HT</th>
                    <th style={{ textAlign: 'center' }}>Avt precedent %</th>
                    <th style={{ textAlign: 'center' }}>Avt cumule %</th>
                    <th style={{ textAlign: 'center' }}>Delta facture %</th>
                    <th style={{ textAlign: 'right' }}>Avt HT (delta)</th>
                    <th style={{ textAlign: 'right' }}>Avt TTC (delta)</th>
                  </tr>
                </thead>
                <tbody>
                  {(inv.lines || []).map((ln, idx) => (
                    <tr key={ln.id || idx}>
                      <td style={{ fontWeight: 600 }}>{ln.description}</td>
                      <td style={{ textAlign: 'right' }}>{Number(ln.price_ht || 0).toLocaleString('fr-FR')} EUR</td>
                      <td style={{ textAlign: 'center', color: '#5E5F66' }}>{Number(ln.previous_advancement || 0).toFixed(0)}%</td>
                      <td style={{ textAlign: 'center', fontWeight: 700, color: '#002FA7' }}>{Number(ln.total_advancement || 0).toFixed(0)}%</td>
                      <td style={{ textAlign: 'center', fontWeight: 700, color: '#1565C0' }}>+{Number(ln.monthly_advancement || 0).toFixed(0)}%</td>
                      <td style={{ textAlign: 'right', fontWeight: 600 }}>{Number(ln.advancement_ht || 0).toLocaleString('fr-FR')} EUR</td>
                      <td style={{ textAlign: 'right', fontWeight: 700, color: '#2E7D32' }}>{Number(ln.advancement_ttc || 0).toLocaleString('fr-FR')} EUR</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ background: '#F4F5F7', fontWeight: 700 }}>
                    <td colSpan={5} style={{ textAlign: 'right' }}>TOTAL DE LA SITUATION</td>
                    <td style={{ textAlign: 'right' }}>{(inv.total_ht || 0).toLocaleString('fr-FR')} EUR</td>
                    <td style={{ textAlign: 'right', color: '#2E7D32' }}>{(inv.total_ttc || 0).toLocaleString('fr-FR')} EUR</td>
                  </tr>
                </tfoot>
              </table>
            </div>
            {inv.notes && <div style={{ margin: '14px 20px', padding: '10px 14px', backgroundColor: '#F4F5F7', borderRadius: 4, fontSize: '0.88rem', color: '#5E5F66' }}>{inv.notes}</div>}
          </>
        )}
      </div>
    </div>
  );
};

export const InvoicesTab = ({ invoices, navigate, onRefresh }) => {
  const [consultId, setConsultId] = useState(null);
  const [pdfViewer, setPdfViewer] = useState({ show: false, url: '', title: '' });
  const [updatingId, setUpdatingId] = useState(null);

  const handleViewPdf = async (inv) => {
    try {
      const res = await axios.get(`${API_URL}/api/invoicing/${inv.id}/export/pdf`, { withCredentials: true, responseType: 'blob' });
      const blobUrl = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      setPdfViewer({ show: true, url: blobUrl, title: `Facture ${inv.number}` });
    } catch { toast.error('Impossible de charger le PDF'); }
  };
  const closePdfViewer = () => {
    if (pdfViewer.url) URL.revokeObjectURL(pdfViewer.url);
    setPdfViewer({ show: false, url: '', title: '' });
  };
  const handleSendEmail = async (inv) => {
    if (!window.confirm(`Envoyer la facture ${inv.number} au client par email ?`)) return;
    try {
      await axios.post(`${API_URL}/api/invoicing/${inv.id}/send-to-client`, {}, { withCredentials: true });
      toast.success('Facture envoyee au client');
      if (onRefresh) onRefresh();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur envoi email'); }
  };
  const handleEdit = (inv) => {
    navigate(`/invoicing?tab=facturation&id=${inv.id}&edit=1`);
  };
  const handleDelete = async (inv) => {
    if (!window.confirm(`Supprimer la facture ${inv.number} ? Cette action est irreversible.`)) return;
    try {
      await axios.delete(`${API_URL}/api/invoicing/${inv.id}`, { withCredentials: true });
      toast.success('Facture supprimee');
      if (consultId === inv.id) setConsultId(null);
      if (onRefresh) onRefresh();
    } catch { toast.error('Erreur suppression'); }
  };
  const handleStatusChange = async (inv, newStatus) => {
    setUpdatingId(inv.id);
    try {
      await axios.put(`${API_URL}/api/invoicing/${inv.id}`, { status: newStatus }, { withCredentials: true });
      toast.success('Statut mis a jour');
      if (onRefresh) onRefresh();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur statut'); }
    finally { setUpdatingId(null); }
  };

  return (
  <div className="card">
    <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <h3 className="card-title">Situations Travaux ({invoices.length})</h3>
      <button className="btn btn-primary btn-sm" onClick={() => navigate('/invoicing?tab=facturation')} data-testid="go-to-invoicing-btn"><Plus size={14} /> Nouvelle facture</button>
    </div>
    {invoices.length > 0 ? (
      <table className="data-table" data-testid="situations-table">
        <thead><tr><th>N Facture</th><th>Date</th><th>Postes</th><th style={{ textAlign: 'right' }}>Avancement HT</th><th style={{ textAlign: 'right' }}>Avancement TTC</th><th>Statut</th><th style={{ minWidth: 230 }}></th></tr></thead>
        <tbody>
          {invoices.map((inv) => {
            const cfg = STATUS_CONFIG[inv.status] || STATUS_CONFIG.brouillon;
            return (
              <tr
                key={inv.id}
                style={{ cursor: 'pointer' }}
                onClick={() => setConsultId(inv.id)}
                data-testid={`situation-row-${inv.id}`}
                title="Cliquer pour consulter le detail de la situation"
              >
                <td style={{ fontWeight: '700', color: '#002FA7' }}>{inv.number}</td>
                <td>{inv.date}</td>
                <td>{inv.lines_count}</td>
                <td style={{ textAlign: 'right', fontWeight: '600' }}>{inv.total_advancement_ht.toLocaleString('fr-FR')} EUR</td>
                <td style={{ textAlign: 'right', fontWeight: '700', color: '#2E7D32' }}>{inv.total_advancement_ttc.toLocaleString('fr-FR')} EUR</td>
                <td><span style={{ padding: '3px 10px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: '600', backgroundColor: cfg.bg, color: cfg.color }}>{cfg.label}</span></td>
                <td onClick={(e) => e.stopPropagation()}>
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    <button className="btn btn-secondary btn-sm" onClick={() => setConsultId(inv.id)} title="Consulter" data-testid={`situation-consult-btn-${inv.id}`}><Eye size={13} /></button>
                    <button className="btn btn-secondary btn-sm" onClick={() => handleViewPdf(inv)} title="Voir PDF" data-testid={`situation-pdf-${inv.id}`}><FilePdf size={13} /></button>
                    {inv.status === 'brouillon' && (
                      <button className="btn btn-secondary btn-sm" onClick={() => handleStatusChange(inv, 'acceptee')} disabled={updatingId === inv.id} title="Marquer acceptee" data-testid={`situation-accept-${inv.id}`} style={{ background: '#FEF3C7', color: '#92400E', borderColor: '#FDE68A' }}>
                        <CheckCircle size={13} />
                      </button>
                    )}
                    {(inv.status === 'brouillon' || inv.status === 'acceptee') && (
                      <button className="btn btn-primary btn-sm" onClick={() => handleStatusChange(inv, 'emise')} disabled={updatingId === inv.id} title="Emettre">Emettre</button>
                    )}
                    {inv.status === 'emise' && (
                      <button className="btn btn-primary btn-sm" onClick={() => handleStatusChange(inv, 'payee')} disabled={updatingId === inv.id} title="Marquer payee" style={{ background: '#047857', borderColor: '#047857' }}>Payee</button>
                    )}
                    <button className="btn btn-secondary btn-sm" onClick={() => handleSendEmail(inv)} title="Envoyer par email" data-testid={`situation-send-${inv.id}`} style={{ background: '#1D4ED8', color: 'white', borderColor: '#1D4ED8' }}><PaperPlaneTilt size={13} /></button>
                    <button className="btn btn-secondary btn-sm" onClick={() => handleEdit(inv)} title="Modifier" data-testid={`situation-edit-${inv.id}`}><PencilSimple size={13} /></button>
                    <button className="btn btn-danger btn-sm" onClick={() => handleDelete(inv)} title="Supprimer" data-testid={`situation-delete-${inv.id}`}><Trash size={13} /></button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr style={{ backgroundColor: '#F4F5F7', fontWeight: '700' }}>
            <td colSpan={3} style={{ textAlign: 'right' }}>TOTAL</td>
            <td style={{ textAlign: 'right' }}>{(invoices.reduce((s, i) => s + i.total_advancement_ht, 0) || 0).toLocaleString('fr-FR')} EUR</td>
            <td style={{ textAlign: 'right', color: '#2E7D32' }}>{(invoices.reduce((s, i) => s + i.total_advancement_ttc, 0) || 0).toLocaleString('fr-FR')} EUR</td>
            <td colSpan={2}></td>
          </tr>
        </tfoot>
      </table>
    ) : (
      <div style={{ padding: '40px', textAlign: 'center', color: '#5E5F66' }}>
        <Receipt size={40} style={{ marginBottom: '12px', opacity: 0.5 }} />
        <p>Aucune facture pour ce chantier</p>
        <button className="btn btn-primary btn-sm" onClick={() => navigate('/invoicing?tab=facturation')} style={{ marginTop: '12px' }}><Plus size={14} /> Creer dans Facturation</button>
      </div>
    )}
    {consultId && <SituationConsultModal invoiceId={consultId} onClose={() => setConsultId(null)} />}
    <PdfViewerModal show={pdfViewer.show} url={pdfViewer.url} title={pdfViewer.title} onClose={closePdfViewer} />
  </div>
  );
};

export const PhotosTab = ({ photos, onUpload, onUpdateComment, onDelete, editingPhotoId, setEditingPhotoId, editPhotoComment, setEditPhotoComment, setPreviewPhoto }) => (
  <div>
    {photos.length > 0 ? (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' }}>
        {photos.map((photo) => (
          <div key={photo.id} className="card" style={{ overflow: 'hidden' }}>
            <div style={{ position: 'relative', cursor: 'pointer' }} onClick={() => setPreviewPhoto(photo)}>
              <img src={`${API_URL}${photo.url}`} alt={photo.original_filename} style={{ width: '100%', height: '200px', objectFit: 'cover', display: 'block' }} onError={(e) => { e.target.style.display = 'none'; }} />
              <div style={{ position: 'absolute', top: '8px', right: '8px', backgroundColor: 'rgba(0,0,0,0.6)', color: 'white', padding: '2px 8px', borderRadius: '4px', fontSize: '0.7rem' }}><Eye size={12} /> Agrandir</div>
            </div>
            <div style={{ padding: '12px' }}>
              {editingPhotoId === photo.id ? (
                <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                  <input type="text" className="form-input" value={editPhotoComment} onChange={(e) => setEditPhotoComment(e.target.value)} style={{ flex: 1, fontSize: '0.85rem' }} autoFocus />
                  <button className="btn btn-primary btn-sm" onClick={() => onUpdateComment(photo.id)}>OK</button>
                  <button className="btn btn-secondary btn-sm" onClick={() => setEditingPhotoId(null)}><X size={14} /></button>
                </div>
              ) : (
                <div style={{ marginBottom: '8px' }}>
                  {photo.comment ? (
                    <p style={{ margin: 0, fontSize: '0.85rem', color: '#0A0B10' }}><ChatCircle size={14} style={{ marginRight: '4px', verticalAlign: 'middle' }} />{photo.comment}</p>
                  ) : (
                    <p style={{ margin: 0, fontSize: '0.85rem', color: '#9E9E9E', fontStyle: 'italic' }}>Aucun commentaire</p>
                  )}
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.75rem', color: '#5E5F66' }}>
                <span>{photo.uploaded_by_name} - {photo.created_at?.slice(0, 10)}</span>
                <div style={{ display: 'flex', gap: '4px' }}>
                  <button className="btn btn-secondary btn-sm" onClick={(e) => { e.stopPropagation(); setEditingPhotoId(photo.id); setEditPhotoComment(photo.comment || ''); }}><PencilSimple size={14} /></button>
                  <button className="btn btn-danger btn-sm" onClick={(e) => { e.stopPropagation(); onDelete(photo.id); }}><Trash size={14} /></button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    ) : (
      <div className="card">
        <div style={{ padding: '60px', textAlign: 'center', color: '#5E5F66' }}>
          <Camera size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
          <p>Aucune photo pour ce chantier</p>
          <button className="btn btn-primary" onClick={onUpload} style={{ marginTop: '12px' }}><Plus size={16} /> Ajouter une photo</button>
        </div>
      </div>
    )}
  </div>
);
